import { styled } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';
import IconButton from '@mui/material/IconButton';
import CancelIcon from '@/assets/cancel.svg?react';
import { useState } from 'react';
import DownloadManagerItem from './DownloadManagerItem';
import { DownloadManagerSummary } from './DownloadManagerSummary';
import { StyledArrowIcon } from '../UploadManager/UploadManager';
import { useDownload } from '@/hooks/useDownload';
import { useReportBottomOverlay } from '@/lib/bottomOverlays';

// Gap between the panel and the edges of the viewport.
const MARGIN = 20;

const DownloadManager = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [rootElement, setRootElement] =
    useState<HTMLDivElement | null>(null);

  // Nothing else claims the bottom-left corner, the basket bar shortens
  useReportBottomOverlay('downloadManager', 'left', rootElement);

  const {
    list,
    retryDownload,
    dismissDownload,
    managerOpen,
    setManagerOpen,
  } = useDownload();

  return (
    <Root ref={setRootElement} open={managerOpen}>
      <Collapsible collapsed={collapsed}>
        <Content>
          <Title>
            <Label id="download-manager-title">
              <FormattedMessage
                id="downloads"
                defaultMessage="Downloads"
              />
            </Label>
            <IconButton
              sx={{ padding: 0, width: 28, height: 28 }}
              onClick={() => setCollapsed(!collapsed)}
              aria-label="Collapse downloads"
            >
              <StyledArrowIcon isOpen={collapsed} />
            </IconButton>
            <IconButton
              sx={{
                marginLeft: 1,
                padding: 0,
                width: 28,
                height: 28,
              }}
              onClick={() => setManagerOpen(false)}
              aria-label="Close downloads"
            >
              <CancelIcon />
            </IconButton>
          </Title>
          <Body>
            {list.map((download) => (
              <DownloadManagerItem
                key={download.id}
                status={download.status}
                title={download.title}
                progress={download.progress}
                preparing={download.preparing}
                files={download.files}
                onRetry={() => retryDownload(download.id)}
                onDismiss={() => dismissDownload(download.id)}
              />
            ))}
          </Body>
        </Content>
      </Collapsible>
      <Footer>
        <DownloadManagerSummary
          list={list}
          onDismissAll={() =>
            list
              .filter((download) => download.status !== 'pending')
              .forEach((download) => dismissDownload(download.id))
          }
          collapsed={collapsed}
          onExpand={() => setCollapsed(false)}
        />
      </Footer>
    </Root>
  );
};

const Root = styled('div', {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open: boolean }>(({ theme, open }) => ({
  position: 'fixed',
  display: open ? 'flex' : 'none',
  flexDirection: 'column',
  bottom: MARGIN,
  left: MARGIN,
  // Never wider or taller than the viewport it floats over.
  width: `min(450px, calc(100vw - ${MARGIN * 2}px))`,
  backgroundColor: theme.palette.black,
  borderRadius: theme.radii.xs,
  boxShadow: theme.shadows[2],
  zIndex: theme.zIndexes.downloadManager,
  color: theme.palette.white,
  overflow: 'hidden',
  maxHeight: `min(500px, calc(100vh - ${MARGIN * 2}px))`,
}));

// Title and list collapse together, leaving only the footer. Animating the
// grid row between 0fr and 1fr sizes it to its content, so nothing has to be
// measured.
const Collapsible = styled('div', {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<{ collapsed: boolean }>(({ collapsed }) => ({
  display: 'grid',
  gridTemplateRows: collapsed ? '0fr' : '1fr',
  flex: '1 1 auto',
  minHeight: 0,
  transition: 'grid-template-rows 0.3s ease-in-out',
}));

const Content = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  overflow: 'hidden',
});

const Title = styled('div')(({ theme }) => ({
  display: 'flex',
  padding: `${theme.spacing(3)} ${theme.spacing(2)} ${theme.spacing(
    3,
  )}`,

  svg: {
    width: '24px',
    height: '24px',

    path: {
      stroke: theme.palette.white,
    },
  },
}));

const Label = styled('div')({
  flexGrow: 1,
  fontSize: '24px',
  lineHeight: '28px',
  fontWeight: 600,
});

const Body = styled('div')(({ theme }) => ({
  flexGrow: 1,
  flexShrink: 1,
  minHeight: 0,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  padding: `0 ${theme.spacing(3)}`,
  gap: theme.spacing(1),
}));

const Footer = styled('div')(({ theme }) => ({
  flexShrink: 0,
  padding: theme.spacing(1),
}));

export default DownloadManager;
